import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../../../utils/GetCookie';
import ErrorPopup from '../../../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../../../utils/popup/ProcessingPopup';
import DocumentPreview from '../../../../utils/DocumentPreview';
import PaginationControls from '../../../../utils/PaginationControls';
import authNavigate from '../../../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const ProtocolDocSelector = ({
    projectId,
    onClose,
    onImportSuccess,
}) => {
    const navigate = useNavigate();

    const API_BASE_URL = '/pceapi/slr/form';
    const headers = {
        Authorization: GetCookie('access_token'),
    };

    // Collections + docs state
    const [threads,  setThreads]  = useState([]);
    const [total,    setTotal]    = useState(0);
    const [page,     setPage]     = useState(1);
    const [size,     setSize]     = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search,   setSearch]   = useState('');

    const [expandedThreads, setExpandedThreads] = useState({});
    const [selectedDocId,   setSelectedDocId]   = useState(null);
    const [selectedDocName, setSelectedDocName] = useState(null);

    // Preview
    const [previewOpen,        setPreviewOpen]        = useState(false);
    const [previewDocId,       setPreviewDocId]       = useState(null);
    const [previewDocumentName,setPreviewDocumentName]= useState(null);

    // UI flags
    const [isLoading,              setIsLoading]              = useState(false);
    const [isImporting,            setIsImporting]            = useState(false);
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);

    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [errorMessage,        setErrorMessage]        = useState('');

    const closeErrorPopup = () => {
        setIsErrorPopupVisible(false);
        setErrorMessage('');
    };

    // ── Fetch on mount ────────────────────────────────────────
    useEffect(() => {
        setPage(1);
        setSize(25);
        setSearch('');
        setSearchInput('');
        setSelectedDocId(null);
        setSelectedDocName(null);
        setExpandedThreads({});
        fetchThreads(1, 25, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // ── Fetch collections ─────────────────────────────────────
    const fetchThreads = async (
        pageNumber = page,
        pageSize   = size,
        searchText = search,
    ) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/list-data-collections`,
                { page: pageNumber, size: pageSize, search: searchText || undefined },
                { headers },
            );

            const data = response.data;

            if (data.status === 'NotAuthorized') {
                authNavigate('/login', 'iam', navigate);
                return;
            }
            if (data.status === 'failed') {
                setErrorMessage(data.message || 'Failed to load data collections');
                setIsErrorPopupVisible(true);
                return;
            }

            setThreads(data.data   || []);
            setTotal(data.total    || 0);
            setPage(data.page      || pageNumber);
            setSize(data.size      || pageSize);
        } catch (error) {
            console.error('Error fetching data collections:', error);
            setErrorMessage(error.message || 'Failed to load data collections');
            setIsErrorPopupVisible(true);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Search ────────────────────────────────────────────────
    const handleSearchInputChange = (e) => setSearchInput(e.target.value);

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
    };

    const handleSearch = () => {
        const trimmed = (searchInput || '').trim();
        setSearch(trimmed);
        setPage(1);
        fetchThreads(1, size, trimmed);
    };

    // ── Pagination ────────────────────────────────────────────
    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(1);
        fetchThreads(1, newSize, search);
    };

    const handlePageChange = (direction) => {
        const totalPages = Math.max(1, Math.ceil(total / size));
        let newPage = page;
        if (direction === 'prev') newPage = Math.max(1, page - 1);
        if (direction === 'next') newPage = Math.min(totalPages, page + 1);
        if (newPage !== page) {
            setPage(newPage);
            fetchThreads(newPage, size, search);
        }
    };

    // ── Expand / collapse ─────────────────────────────────────
    const toggleThreadExpansion = (threadId) => {
        setExpandedThreads((prev) => ({ ...prev, [threadId]: !prev[threadId] }));
    };

    // ── Selection ─────────────────────────────────────────────
    const handleSelectDoc = (doc) => {
        setSelectedDocId(doc.id);
        setSelectedDocName(doc.document_name);
    };

    // ── Preview ───────────────────────────────────────────────
    const openPreview = (doc) => {
        setPreviewDocId(doc.id);
        setPreviewDocumentName(doc.document_name);
        setPreviewOpen(true);
    };
    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewDocId(null);
        setPreviewDocumentName(null);
    };

    // ── Import ────────────────────────────────────────────────
    const handleImport = async () => {
        if (!selectedDocId) {
            setErrorMessage('Please select a document to import.');
            setIsErrorPopupVisible(true);
            return;
        }
        if (!projectId) {
            setErrorMessage('Project information is missing.');
            setIsErrorPopupVisible(true);
            return;
        }

        setIsImporting(true);
        setIsProcessingPopupVisible(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/import-document-as-protocol`,
                { slr_fabric_id: projectId, document_id: selectedDocId },
                { headers },
            );

            const data = response.data;

            if (data.status === 'NotAuthorized') { authNavigate('/login', 'iam', navigate); return; }
            if (data.status === 'failed') {
                setErrorMessage(data.message || 'Failed to load document');
                setIsErrorPopupVisible(true);
                return;
            }

            // Populate the editor — nothing is saved yet.
            // The user must press Save to persist the protocol.
            onImportSuccess(data.markdown || '');
            onClose();
        } catch (error) {
            console.error('Error loading document as protocol:', error);
            setErrorMessage(error.message || 'Failed to load document');
            setIsErrorPopupVisible(true);
        } finally {
            setIsImporting(false);
            setIsProcessingPopupVisible(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / size));

    // ── Render ────────────────────────────────────────────────
    return (
        <>
            <div className="ev-card-4 ev-padding-small ev-radius-medium">

                {/* Info banner */}
                <div className="ev-alert-card-warning mb-2">
                    <p className="mb-0">
                        Select a document (.pdf/.docx) from your Data Collections below to import it as the
                        screening protocol. This will replace any existing protocol content.
                    </p>
                </div>

                {/* Search + pagination */}
                <div className="row mb-2">
                    {/* Search */}
                    <div className="col-5">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control search-input ms-0"
                                placeholder="Search Data Collections"
                                value={searchInput}
                                onChange={handleSearchInputChange}
                                onKeyPress={handleSearchKeyPress}
                            />
                            <button
                                type="button"
                                className="ev-btn-lightblue-fill-round search-bar-button"
                                onClick={handleSearch}
                            >
                                <i className="bi bi-search"></i>
                            </button>
                        </div>
                    </div>

                    {/* Pagination controls */}
                    <div className="col-7 d-flex justify-content-end align-items-center">
                        <div className="d-flex align-items-center">
                            <button
                                type="button"
                                className="ev-btn-light-fill ev-btn-xsmall"
                                disabled={page <= 1}
                                onClick={() => handlePageChange('prev')}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <span className="ev-xsmall-p-dark mx-2">
                                Page {page}/{totalPages}
                            </span>
                            <button
                                type="button"
                                className="ev-btn-light-fill ev-btn-xsmall"
                                disabled={page >= totalPages}
                                onClick={() => handlePageChange('next')}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>

                        <div className="d-flex align-items-center ms-2">
                            <PaginationControls
                                initialSize={size}
                                onSizeChange={handleSizeChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Collections & docs list */}
                <div className="scrollable-element vh-25">
                    {isLoading ? (
                        <div className="text-center p-3 mt-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status" />
                        </div>
                    ) : threads.length === 0 ? (
                        <p className="ev-small-p-blue text-center mt-2">No Data Collections found</p>
                    ) : (
                        <div className="thread-tree">
                            {threads.map((thread) => {
                                const docs          = thread.importable_documents || [];
                                const importableCount = thread.importable_count ?? docs.length;

                                return (
                                    <div key={thread.id} className="mb-2">
                                        {/* Collection header row */}
                                        <div className="d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-sm ev-btn-text-only-fill"
                                                onClick={() => toggleThreadExpansion(thread.id)}
                                            >
                                                <i className={`bi ${expandedThreads[thread.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                            </button>

                                            <div
                                                onClick={() => toggleThreadExpansion(thread.id)}
                                                className="cursor-pointer"
                                            >
                                                <span className="ev-small-p-blue">{thread.name}</span>
                                                &nbsp;
                                                <span className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                                                    {thread.thread_access_level}
                                                </span>
                                                <span className="ev-message-card1 ms-1 ev-btn-xsmall ev-fs-11">
                                                    {importableCount} docs
                                                </span>
                                            </div>
                                        </div>

                                        {/* Docs under this collection */}
                                        {expandedThreads[thread.id] && importableCount > 0 && (
                                            <div className="ms-4 mt-0">
                                                {docs.map((doc) => (
                                                    <div key={doc.id} className="mb-1 mt-1">
                                                        <div className="d-flex align-items-start">
                                                            {/* Radio */}
                                                            <input
                                                                type="radio"
                                                                className="form-check-input me-2 flex-shrink-0"
                                                                name="selectedProtocolDoc"
                                                                checked={selectedDocId === doc.id}
                                                                onChange={() => handleSelectDoc(doc)}
                                                            />

                                                            {/* Name + preview */}
                                                            <div className="d-flex align-items-start flex-nowrap">
                                                                <span className="ev-xsmall-p-dark text-break">
                                                                    {doc.document_name}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="ev-btn-lightblue-fill-round ev-btn-xsmall ms-2 flex-shrink-0"
                                                                    onClick={() => openPreview(doc)}
                                                                >
                                                                    <i className="bi bi-eye"></i> view
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="d-flex justify-content-end mt-1">
                    <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-bold-reg"
                        onClick={handleImport}
                        disabled={!selectedDocId || isImporting}
                    >
                        {isImporting ? 'Loading…' : 'Import Protocol'}
                    </button>
                    <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-bold-reg ms-2"
                        onClick={onClose}
                        disabled={isImporting}
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {/* Preview & popups */}
            <DocumentPreview
                isOpen={previewOpen}
                onClose={closePreview}
                docId={previewDocId}
                documentName={previewDocumentName}
            />

            {isProcessingPopupVisible && (
                <ProcessingPopup message="Importing Protocol..." />
            )}

            {isErrorPopupVisible && (
                <ErrorPopup onClose={closeErrorPopup} message={errorMessage} />
            )}
        </>
    );
};

export default ProtocolDocSelector;